'use client';

import { isTRPCClientErrorWithCode } from '@/lib/is-trpc-client-error-with-code';
import { isDevEnv } from '@/lib/utils';
import { type AppRouter } from '@/server/api/root';
import { API_HEADERS_KEY } from '@/server/config/constants';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
    httpBatchLink,
    HTTPHeaders,
    httpLink,
    loggerLink,
    splitLink,
} from '@trpc/client';
import { createTRPCReact } from '@trpc/react-query';
import { useMemo } from 'react';
import { transformer } from './shared';

export const api = createTRPCReact<AppRouter>({ abortOnUnmount: true });

type Props = {
    children: React.ReactNode;
};

/**
 * Danh sách các path mà sẽ sử dụng httpLink thay vì httpBatchLink
 *
 * EXP: API dossier.Query_CountByFilter gọi sẽ lâu hơn các api khác khoảng 2, 3s
 *      Nếu sử dụng httpBatchLink thì sẽ chờ tất cả các api khác chạy xong mới chạy api này
 *      Điều này sẽ làm giảm hiệu suất của trang web
 *      Do đó, cần sử dụng httpLink cho các api nặng như vậy
 */
const TRPC_HEAVY_PATHS = [
    'dossier.Query_CountByFilter',
    'dossier.Query_EMPGetSingle',
    'dossier.Query_EMPGetPage',
    'administrativeUnit.Query_GetTree',
];

function makeQueryClient() {
    return new QueryClient({
        defaultOptions: {
            queries: {
                retry(failureCount, error) {
                    if (
                        isTRPCClientErrorWithCode(error) &&
                        error.data.code === 'UNAUTHORIZED'
                    ) {
                        return false;
                    }
                    return failureCount < 2;
                },
                // Tắt đi refetching khi window focus
                refetchOnWindowFocus: false,
                // refetchOnMount: false,
            },
        },
    });
}

let clientQueryClientSingleton: QueryClient;
function getQueryClient() {
    if (typeof window === 'undefined') {
        // Server: always make a new query client
        return makeQueryClient();
    }
    // Browser: use singleton pattern to keep the same query client
    return (clientQueryClientSingleton ??= makeQueryClient());
}

const defHeaders: HTTPHeaders = {
    [API_HEADERS_KEY.Accept_Language]: 'vi,*;q=0.9',
};

export const TRPCReactProvider: React.FC<Props> = (props) => {
    // NOTE: Avoid useState when initializing the query client if you don't
    //       have a suspense boundary between this and the code that may
    //       suspend because React will throw away the client on the initial
    //       render if it suspends and there is no boundary
    const queryClient = getQueryClient();

    const trpcClient = useMemo(
        () =>
            api.createClient({
                links: [
                    loggerLink({
                        enabled: (op) =>
                            isDevEnv() ||
                            (op.direction === 'down' &&
                                op.result instanceof Error),
                    }),

                    splitLink({
                        condition(op) {
                            if (
                                op.type === 'query' &&
                                TRPC_HEAVY_PATHS.includes(op.path)
                            ) {
                                // Sử dụng httpLink cho các path nặng
                                return true;
                            }

                            // Sử dụng httpBatchLink cho các path nhẹ
                            return false;
                        },
                        true: httpLink({
                            url: '/api/trpc',
                            transformer,
                            headers: defHeaders,
                        }),
                        false: httpBatchLink({
                            url: '/api/trpc',
                            maxURLLength: 2048, // reduced to half
                            transformer,
                            // methodOverride: 'POST',
                            headers: defHeaders,
                        }),
                    }),
                ],
            }),
        []
    );

    return (
        <api.Provider client={trpcClient} queryClient={queryClient}>
            <QueryClientProvider client={queryClient}>
                {props.children}
            </QueryClientProvider>
        </api.Provider>
    );
};
